/**
 * Resilient POST helper.
 *
 * Auth endpoints in production sometimes fail with:
 *   - "postMessage on 'Window': Request object could not be cloned"
 *   - "Failed to execute 'text' on 'Response': body stream already read"
 *   - "Network error" (CORS: wildcard origin + credentials = rejected)
 *
 * These are caused by browser extensions or platform monitoring scripts that
 * wrap window.fetch, OR by ingress proxies forcing `Access-Control-Allow-Origin: *`
 * which conflicts with credentialed requests.
 *
 * Solution: use XMLHttpRequest (not wrapped) WITHOUT `withCredentials` — our auth
 * endpoints use JWT tokens (Authorization header / localStorage), not cookies,
 * so credentials are not needed and we sidestep the CORS wildcard + credentials
 * incompatibility entirely.
 *
 * Returns { status, ok, data }.
 */
export function postJsonResilient(url, payload, { withCredentials = false } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    if (withCredentials) xhr.withCredentials = true;
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch {}
      resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data });
    };
    xhr.onerror = () => reject(new Error("Network error. Please check your connection and try again."));
    xhr.ontimeout = () => reject(new Error("Request timed out. Please try again."));
    xhr.timeout = 20000;
    xhr.send(JSON.stringify(payload));
  });
}
